import i18next from 'i18next';

import { GENERIC_MODAL_CANCEL_BUTTON } from '@commons/Modals/GenericModal/genericModalActions';
import { getConfirmationModal } from '@commons/Modals/ConfirmationModal';

/**
 * Confirmation asked before deleting a draft order — the deletion is a soft delete server-side, but
 * the order leaves the list for good from the user standpoint.
 *
 * @param {Object} params
 * @param {Function} params.onConfirm - Deletion handler
 *
 * @returns {Object} The generic modal configuration
 */
export const getDeleteOrderModal = ({ onConfirm }) =>
  getConfirmationModal({
    title: i18next.t('EXTERNAL_ORDERS.LIST.DELETE_MODAL_TITLE'),
    icon: '/images/inpulse/warning-white-small.svg',
    content: i18next.t('EXTERNAL_ORDERS.LIST.DELETE_MODAL_CONTENT'),
    actions: [
      GENERIC_MODAL_CANCEL_BUTTON(),
      {
        key: 1,
        color: 'inpulse-default',
        label: i18next.t('GENERAL.DELETE'),
        icon: '/images/inpulse/trash-white-small.svg',
        handleClick: onConfirm,
      },
    ],
  });

/**
 * Confirmation asked before moving an order forward in the flow. Such a transition is what the user
 * is here for — it is not destructive — hence a success-flavoured modal rather than a warning one.
 *
 * @param {Object} params
 * @param {string} params.title - The modal title
 * @param {string} params.content - The modal content
 * @param {Function} params.onConfirm - Transition handler
 *
 * @returns {Object} The generic modal configuration
 */
const getForwardTransitionModal = ({ title, content, onConfirm }) =>
  getConfirmationModal({
    type: 'success',
    title,
    icon: '/images/inpulse/check-white-small.svg',
    content,
    actions: [
      GENERIC_MODAL_CANCEL_BUTTON(),
      {
        key: 1,
        color: 'inpulse-default',
        label: i18next.t('GENERAL.VALIDATE'),
        icon: '/images/inpulse/check-white-small.svg',
        handleClick: onConfirm,
      },
    ],
  });

/**
 * Confirmation asked before validating a draft, which decrements the future theoretical stock.
 *
 * @param {Object} params
 * @param {Function} params.onConfirm - Validation handler
 *
 * @returns {Object} The generic modal configuration
 */
export const getValidateDraftModal = ({ onConfirm }) =>
  getForwardTransitionModal({
    title: i18next.t('EXTERNAL_ORDERS.FORM.VALIDATE_DRAFT_MODAL_TITLE'),
    content: i18next.t('EXTERNAL_ORDERS.FORM.VALIDATE_DRAFT_MODAL_CONTENT'),
    onConfirm,
  });

/**
 * Confirmation asked before starting the preparation of a `A venir` order.
 *
 * @param {Object} params
 * @param {Function} params.onConfirm - Transition handler
 *
 * @returns {Object} The generic modal configuration
 */
export const getStartPreparationModal = ({ onConfirm }) =>
  getForwardTransitionModal({
    title: i18next.t('EXTERNAL_ORDERS.FORM.START_PREPARATION_MODAL_TITLE'),
    content: i18next.t('EXTERNAL_ORDERS.FORM.START_PREPARATION_MODAL_CONTENT'),
    onConfirm,
  });

/**
 * Confirmation asked before leaving the form with unsaved changes.
 *
 * @param {Object} params
 * @param {Function} params.onConfirm - Handler leaving the form and dropping the changes
 *
 * @returns {Object} The generic modal configuration
 */
export const getLeaveOrderFormModal = ({ onConfirm }) =>
  getConfirmationModal({
    title: i18next.t('EXTERNAL_ORDERS.FORM.LEAVE_MODAL_TITLE'),
    icon: '/images/inpulse/warning-white-small.svg',
    content: i18next.t('EXTERNAL_ORDERS.FORM.LEAVE_MODAL_CONTENT'),
    actions: [
      GENERIC_MODAL_CANCEL_BUTTON(),
      {
        key: 1,
        color: 'inpulse-default',
        label: i18next.t('GENERAL.LEAVE'),
        icon: '/images/inpulse/close-white-small.svg',
        handleClick: onConfirm,
      },
    ],
  });

/**
 * Confirmation asked before moving an order back to a previous status. Every transition shares the
 * same shape, only its wording changes with the status it targets.
 *
 * @param {Object} params
 * @param {string} params.title - The modal title
 * @param {string} params.content - The modal content
 * @param {Function} params.onConfirm - Transition handler
 *
 * @returns {Object} The generic modal configuration
 */
const getStatusTransitionModal = ({ title, content, onConfirm }) =>
  getConfirmationModal({
    title,
    icon: '/images/inpulse/warning-white-small.svg',
    content,
    actions: [
      GENERIC_MODAL_CANCEL_BUTTON(),
      {
        key: 1,
        color: 'inpulse-default',
        label: i18next.t('GENERAL.CONFIRM'),
        icon: '/images/inpulse/check-white-small.svg',
        handleClick: onConfirm,
      },
    ],
  });

/**
 * Confirmation asked before sending an order back to the draft status, which drops the stock events
 * it generated on its way to `A venir`.
 *
 * @param {Object} params
 * @param {Function} params.onConfirm - Transition handler
 *
 * @returns {Object} The generic modal configuration
 */
export const getRevertToDraftModal = ({ onConfirm }) =>
  getStatusTransitionModal({
    title: i18next.t('EXTERNAL_ORDERS.LIST.REVERT_TO_DRAFT_MODAL_TITLE'),
    content: i18next.t('EXTERNAL_ORDERS.LIST.REVERT_TO_DRAFT_MODAL_CONTENT'),
    onConfirm,
  });

/**
 * Confirmation asked before sending an order back to `A venir`, which drops the delivery note
 * quantities typed since the preparation started.
 *
 * @param {Object} params
 * @param {Function} params.onConfirm - Transition handler
 *
 * @returns {Object} The generic modal configuration
 */
export const getRevertToToComeModal = ({ onConfirm }) =>
  getStatusTransitionModal({
    title: i18next.t('EXTERNAL_ORDERS.LIST.REVERT_TO_TO_COME_MODAL_TITLE'),
    content: i18next.t('EXTERNAL_ORDERS.LIST.REVERT_TO_TO_COME_MODAL_CONTENT'),
    onConfirm,
  });

/**
 * Confirmation asked before sending a prepared order back to `En préparation`, which reopens its
 * delivery note quantities.
 *
 * @param {Object} params
 * @param {Function} params.onConfirm - Transition handler
 *
 * @returns {Object} The generic modal configuration
 */
export const getRevertToInPreparationModal = ({ onConfirm }) =>
  getStatusTransitionModal({
    title: i18next.t('EXTERNAL_ORDERS.LIST.REVERT_TO_IN_PREPARATION_MODAL_TITLE'),
    content: i18next.t('EXTERNAL_ORDERS.LIST.REVERT_TO_IN_PREPARATION_MODAL_CONTENT'),
    onConfirm,
  });
